import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Loader2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RestockDialog = ({ product, onOpenChange, headers, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (product) setForm({
      quantity_added: '',
      total_amount_paid: '',
      purchase_date: new Date().toISOString().slice(0, 10),
      vendor_name: product.vendor_name || '',
      store_address: product.store_address || '',
      purchase_url: product.purchase_url || '',
      notes: '',
      update_replacement_cost: false,
      new_replacement_cost: '',
    });
  }, [product]);

  if (!product) return null;

  const save = async () => {
    if (!form.quantity_added || !form.total_amount_paid || !form.purchase_date) {
      toast.error('Quantity, amount, and date are required'); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity_added: parseFloat(form.quantity_added),
        total_amount_paid: parseFloat(form.total_amount_paid),
        new_replacement_cost: form.update_replacement_cost && form.new_replacement_cost ? parseFloat(form.new_replacement_cost) : undefined,
      };
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] == null) delete payload[k]; });
      await axios.post(`${API_URL}/api/admin/inventory/products/${product.id}/restock`, payload, { headers });
      toast.success(`Restocked ${product.name}`);
      onSaved(); onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restock');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="restock-dialog">
        <DialogHeader><DialogTitle>Restock {product.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <F label="Quantity Added *"><Input type="number" step="1" value={form.quantity_added} onChange={e => set('quantity_added', e.target.value)} data-testid="restock-qty" /></F>
            <F label="Total Paid ($) *"><Input type="number" step="0.01" value={form.total_amount_paid} onChange={e => set('total_amount_paid', e.target.value)} data-testid="restock-paid" /></F>
          </div>
          <F label="Purchase Date *"><Input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Vendor"><Input value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} /></F>
            <F label="Store Address"><Input value={form.store_address} onChange={e => set('store_address', e.target.value)} /></F>
          </div>
          <F label="Purchase URL"><Input value={form.purchase_url} onChange={e => set('purchase_url', e.target.value)} /></F>
          <F label="Notes"><Textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></F>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={form.update_replacement_cost} onCheckedChange={v => set('update_replacement_cost', !!v)} data-testid="restock-update-cost" />
            Update Current Replacement Cost
          </label>
          {form.update_replacement_cost && (
            <F label="New Replacement Cost ($)"><Input type="number" step="0.01" value={form.new_replacement_cost} onChange={e => set('new_replacement_cost', e.target.value)} data-testid="restock-new-cost" /></F>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#F56F05] hover:bg-[#e06505] text-white" data-testid="restock-save">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Restock
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
const F = ({ label, children }) => (<div className="space-y-1"><Label className="text-xs text-zinc-600">{label}</Label>{children}</div>);

export default RestockDialog;
